import clsx from 'clsx'
import { parseISO } from 'date-fns'
import { Calendar } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select, { components, OptionProps } from 'react-select'

import FilterChip from '@/components-latest/primitives/Controls/FilterChip'
import {
  Select as RadixSelect,
  SelectAction,
  SelectContent,
  SelectItem,
  SelectTrigger
} from '@/components-latest/primitives/Controls/Select'
import type { CampaignFilterHandlers } from '@/components-latest/ui/CampaignsPage/useCampaignsFilters'
import DateRangeModal from '@/components-latest/ui/PerformancePage/DateRangeModal'
import {
  CAMPAIGN_DATE_PRESET_LABEL_KEYS,
  CAMPAIGN_DATE_PRESET_OPTIONS,
  CAMPAIGN_STATUS_FILTER_OPTIONS,
  CAMPAIGN_STATUS_LABEL_KEYS,
  CAMPAIGN_TYPE_FILTER_OPTIONS,
  CAMPAIGN_TYPE_LABEL_KEYS,
  formatCampaignFilterDate,
  parseCampaignFilterStatusValue
} from '@/ducks/campaigns/campaigns.filters.helpers'
import type {
  CampaignDateRangePreset,
  CampaignFilters,
  CampaignType
} from '@/ducks/campaigns/campaigns.types'

import Styles from './CampaignsFilters.module.scss'

const CUSTOM_DATE_VALUE = 'custom'
const MENU_MIN_WIDTH = 220

type MultiSelectOption = { value: string; label: string }

type Props = {
  filters: CampaignFilters
  handlers: CampaignFilterHandlers
}

const MultiSelectOptionRow = (props: OptionProps<MultiSelectOption, true>) => {
  const { data, isSelected, innerRef, innerProps } = props

  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={clsx(Styles.multiSelectOption, {
        [Styles.multiSelectOptionSelected]: isSelected
      })}>
      <input
        type="checkbox"
        checked={isSelected}
        readOnly
        className={Styles.multiSelectCheckbox}
      />
      <label>{data.label}</label>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CampaignFilterValueContainer = ({ children, ...props }: any) => (
  <components.ValueContainer {...props}>
    <div className={Styles.chipLabel}>{props.selectProps['aria-label']}</div>
    {children}
  </components.ValueContainer>
)

const MultiSelectFilter = ({
  options,
  value,
  displayLabel,
  onChange,
  testId
}: {
  options: MultiSelectOption[]
  value: string[]
  displayLabel: string
  onChange: (values: string[]) => void
  testId?: string
}) => {
  return (
    <div className={Styles.multiSelect} data-testid={testId}>
      <Select
        aria-label={displayLabel}
        options={options}
        value={options.filter(option => value.includes(option.value))}
        onChange={selected =>
          onChange(selected ? selected.map(option => option.value) : [])
        }
        isMulti
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        isClearable={false}
        isSearchable={false}
        placeholder=""
        controlShouldRenderValue={false}
        classNames={{
          control: () => Styles.multiSelectControl,
          container: () => Styles.multiSelectContainer,
          valueContainer: () => Styles.multiSelectValueContainer,
          dropdownIndicator: () => Styles.multiSelectDropdownIndicator,
          menu: () => Styles.multiSelectMenu
        }}
        styles={{
          menu: base => ({
            ...base,
            minWidth: MENU_MIN_WIDTH,
            width: 'max-content'
          })
        }}
        components={{
          Option: MultiSelectOptionRow,
          ValueContainer: CampaignFilterValueContainer,
          MultiValue: () => null,
          Placeholder: () => null,
          IndicatorSeparator: () => null
        }}
        classNamePrefix="campaign-filter-select"
      />
    </div>
  )
}

const CampaignsFilters = ({ filters, handlers }: Props) => {
  const { t } = useTranslation()
  const [isDateSelectOpen, setIsDateSelectOpen] = useState(false)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)

  const hasTypeFilters = filters.campaignTypes.length > 0
  const hasStatusFilters = filters.statuses.length > 0
  const hasDateFilters =
    filters.dateRange != null ||
    (filters.dateFrom != null && filters.dateTo != null)

  const clearLabel = t('marketing.campaigns.filters.clear')

  const typeOptions = useMemo(
    () =>
      CAMPAIGN_TYPE_FILTER_OPTIONS.map(value => ({
        value,
        label: t(CAMPAIGN_TYPE_LABEL_KEYS[value])
      })),
    [t]
  )

  const statusOptions = useMemo(
    () =>
      CAMPAIGN_STATUS_FILTER_OPTIONS.map(value => ({
        value: String(value),
        label: t(CAMPAIGN_STATUS_LABEL_KEYS[value])
      })),
    [t]
  )

  const dateLabel = filters.dateRange
    ? t(CAMPAIGN_DATE_PRESET_LABEL_KEYS[filters.dateRange])
    : filters.dateFrom && filters.dateTo
    ? `${filters.dateFrom} - ${filters.dateTo}`
    : t('marketing.campaigns.filters.date')

  const dateSelectValue = filters.dateRange
    ? filters.dateRange
    : filters.dateFrom && filters.dateTo
    ? CUSTOM_DATE_VALUE
    : undefined

  const customDateRange =
    filters.dateFrom && filters.dateTo
      ? {
          startDate: parseISO(filters.dateFrom),
          endDate: parseISO(filters.dateTo)
        }
      : undefined

  const handleDateSelectChange = (value: string) => {
    if (value === 'week' || value === 'month') {
      handlers.setDatePreset(value as CampaignDateRangePreset)
      setIsDateSelectOpen(false)
    }
  }

  return (
    <div className={Styles.filtersRow} data-testid="campaigns-filters">
      <FilterChip
        isActive={hasTypeFilters}
        clearLabel={clearLabel}
        onClear={handlers.clearTypes}>
        <MultiSelectFilter
          options={typeOptions}
          value={filters.campaignTypes}
          displayLabel={
            hasTypeFilters
              ? t('marketing.campaigns.filters.types-count', {
                  count: filters.campaignTypes.length
                })
              : t('marketing.campaigns.filters.types')
          }
          onChange={values =>
            handlers.setCampaignTypes(values as CampaignType[])
          }
          testId="campaigns-filter-types"
        />
      </FilterChip>

      <FilterChip
        isActive={hasStatusFilters}
        clearLabel={clearLabel}
        onClear={handlers.clearStatuses}>
        <MultiSelectFilter
          options={statusOptions}
          value={filters.statuses.map(String)}
          displayLabel={
            hasStatusFilters
              ? t('marketing.campaigns.filters.status-count', {
                  count: filters.statuses.length
                })
              : t('marketing.campaigns.filters.status')
          }
          onChange={values =>
            handlers.setCampaignStatuses(
              values.map(parseCampaignFilterStatusValue)
            )
          }
          testId="campaigns-filter-status"
        />
      </FilterChip>

      <FilterChip
        isActive={hasDateFilters}
        clearLabel={clearLabel}
        onClear={handlers.clearDate}>
        <RadixSelect
          value={dateSelectValue}
          onValueChange={handleDateSelectChange}
          open={isDateSelectOpen}
          onOpenChange={setIsDateSelectOpen}>
          <SelectTrigger
            className={Styles.dateSelectTrigger}
            data-testid="campaigns-filter-date">
            <div className={Styles.dateSelectValue}>
              <Calendar width={20} height={20} />
              <span>{dateLabel}</span>
            </div>
          </SelectTrigger>
          <SelectContent
            className={Styles.dateSelectContent}
            align="start"
            sideOffset={4}>
            {CAMPAIGN_DATE_PRESET_OPTIONS.map(dateRange => (
              <SelectItem
                key={dateRange}
                value={dateRange}
                textClassName={Styles.dateSelectItemText}>
                {t(CAMPAIGN_DATE_PRESET_LABEL_KEYS[dateRange])}
              </SelectItem>
            ))}
            <SelectAction
              value={CUSTOM_DATE_VALUE}
              className={Styles.dateSelectItemText}
              onClick={() => {
                setIsDateSelectOpen(false)
                setIsCalendarOpen(true)
              }}>
              {t('marketing.campaigns.filters.custom')}
            </SelectAction>
          </SelectContent>
        </RadixSelect>
        <DateRangeModal
          startDate={customDateRange?.startDate ?? null}
          endDate={customDateRange?.endDate ?? null}
          isOpen={isCalendarOpen}
          toggleIsOpen={() => setIsCalendarOpen(current => !current)}
          onApply={(startDate, endDate) => {
            if (!startDate || !endDate) return

            const dateFrom = formatCampaignFilterDate(startDate)
            const dateTo = formatCampaignFilterDate(endDate)
            handlers.setCustomDateRange(dateFrom, dateTo)
          }}
        />
      </FilterChip>
    </div>
  )
}

export default CampaignsFilters
